import { TMasterCompanyMainSkill } from "@/api/entities/master-company-main-skill.entity";
import { TMasterCompanySpecificBusinessClassification } from "@/api/entities/master-company-specific-business-classification.entity";
import { setEmploymentTrainingCompanyMainSkillApi, setSpecificSkillCompanySpecificBusinessClassificationApi } from "@/api/services/global/master";
import { TGetCompanySpecificIndustryListOutputDto } from "@/api/services/global/master/dtos/companySpecificIndustry/get-master-company-specific-industry-list.dto";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { useQueryClient } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";

type TRows = TGetCompanySpecificIndustryListOutputDto[];

type TClassificationTransform = (_classification: TMasterCompanySpecificBusinessClassification) => TMasterCompanySpecificBusinessClassification;

export const getSpecificSkillKey = (classificationId: number) => `classification-${classificationId}`;

export const getEmploymentTrainingKey = (mainSkillId: number) => `main-skill-${mainSkillId}`;

export const canSetSpecificSkill = (
  industry: TGetCompanySpecificIndustryListOutputDto,
  classification: TMasterCompanySpecificBusinessClassification,
) => Boolean(industry.companySpecificIndustry?.trim() && classification.companySpecificBusinessClassificationName?.trim());

export const canSetEmploymentTraining = (
  industry: TGetCompanySpecificIndustryListOutputDto,
  classification: TMasterCompanySpecificBusinessClassification,
  mainSkill: TMasterCompanyMainSkill,
) => canSetSpecificSkill(industry, classification) && Boolean(industry.sectorCouncil?.trim() && mainSkill.mainSkillName?.trim());

export const getRowTogglingKey = (industry: TGetCompanySpecificIndustryListOutputDto, togglingKeys: string[]) => {
  if (togglingKeys.length === 0) {
    return "";
  }
  const rowKeys = new Set(
    industry.companySpecificBusinessClassifications.flatMap((classification) => [
      getSpecificSkillKey(toNumber(classification.id)),
      ...classification.mainSkills.map((mainSkill) => getEmploymentTrainingKey(toNumber(mainSkill.id))),
    ]),
  );
  return togglingKeys.filter((key) => rowKeys.has(key)).join(",");
};

const updateClassification = (rows: TRows, industryId: number, classificationId: number, transform: TClassificationTransform) =>
  rows.map((industry) =>
    toNumber(industry.id) === industryId
      ? {
          ...industry,
          companySpecificBusinessClassifications: industry.companySpecificBusinessClassifications.map((classification) =>
            toNumber(classification.id) === classificationId ? transform(classification) : classification,
          ),
        }
      : industry,
  );

const toggleSpecificSkillOf: TClassificationTransform = (classification) => ({
  ...classification,
  isSpecificSkill: !classification.isSpecificSkill,
});

const toggleEmploymentTrainingOf =
  (mainSkillId: number): TClassificationTransform =>
  (classification) => ({
    ...classification,
    mainSkills: classification.mainSkills.map((mainSkill) =>
      toNumber(mainSkill.id) === mainSkillId ? { ...mainSkill, isEmploymentTraining: !mainSkill.isEmploymentTraining } : mainSkill,
    ),
  });

export const useCompanySpecificIndustryVisaFlags = (setRows: Dispatch<SetStateAction<TRows>>) => {
  const queryClient = useQueryClient();
  const [togglingKeys, setTogglingKeys] = useState<string[]>([]);
  const togglingKeysRef = useRef<Set<string>>(new Set());

  const runToggle = useCallback(
    async (key: string, industryId: number, classificationId: number, transform: TClassificationTransform, request: () => Promise<unknown>) => {
      if (togglingKeysRef.current.has(key)) {
        return;
      }
      togglingKeysRef.current.add(key);
      setTogglingKeys((prev) => [...prev, key]);
      setRows((prev) => updateClassification(prev, industryId, classificationId, transform));

      try {
        await request();
        toast.success(API_SUCCESS_MESSAGE);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_COMPANY_SPECIFIC_INDUSTRY] });
      } catch (error) {
        setRows((prev) => updateClassification(prev, industryId, classificationId, transform));
        showError(error);
      } finally {
        togglingKeysRef.current.delete(key);
        setTogglingKeys((prev) => prev.filter((item) => item !== key));
      }
    },
    [queryClient, setRows],
  );

  const toggleSpecificSkill = useCallback(
    (industryId: number, classification: TMasterCompanySpecificBusinessClassification) => {
      const classificationId = toNumber(classification.id);
      return runToggle(getSpecificSkillKey(classificationId), industryId, classificationId, toggleSpecificSkillOf, () =>
        setSpecificSkillCompanySpecificBusinessClassificationApi(industryId, classificationId, {
          isSpecificSkill: !classification.isSpecificSkill,
        }),
      );
    },
    [runToggle],
  );

  const toggleEmploymentTraining = useCallback(
    (industryId: number, classificationId: number, mainSkill: TMasterCompanyMainSkill) => {
      const mainSkillId = toNumber(mainSkill.id);
      return runToggle(getEmploymentTrainingKey(mainSkillId), industryId, classificationId, toggleEmploymentTrainingOf(mainSkillId), () =>
        setEmploymentTrainingCompanyMainSkillApi(industryId, classificationId, mainSkillId, {
          isEmploymentTraining: !mainSkill.isEmploymentTraining,
        }),
      );
    },
    [runToggle],
  );

  const isTogglingSpecificSkill = useCallback(
    (classificationId: number) => togglingKeys.includes(getSpecificSkillKey(classificationId)),
    [togglingKeys],
  );

  const isTogglingEmploymentTraining = useCallback(
    (mainSkillId: number) => togglingKeys.includes(getEmploymentTrainingKey(mainSkillId)),
    [togglingKeys],
  );

  return { toggleSpecificSkill, toggleEmploymentTraining, isTogglingSpecificSkill, isTogglingEmploymentTraining, togglingKeys };
};
